import { useRouter } from "next/router";
import React, { useRef } from "react";
import fs from "fs";
import path from "path";
import matter from "gray-matter";
import { serialize } from "next-mdx-remote/serialize";
import Image from "next/image";
import { MDXRemote } from "next-mdx-remote";
import Layout from "components/Layout";
import classes from "./blog.module.scss";
import BlogList from "components/Blogs/BlogList";
import SEO from "components/Seo";
import { generateArticleSchema } from "data/Schema/blogSchema";
// import SocialShare from "components/Blogs/SocialShare";
// import NextImage from "components/NextImage";
// import FAQ from "page-components/home/FAQ";
// import ItemListSchema from "components/Blogs/ItemListSchema";
import BlogImage from "components/Blogs/BlogImage";
import FAQ from "components/Faq";
import DownloadBtn from "components/Blogs/DownloadBtn";

const blogsDirectory = path.join(process.cwd(), "blogs");
export async function getStaticPaths() {
  const mdFiles = fs.readdirSync(blogsDirectory, {
    withFileTypes: true,
  });
  let paths = [];

  for (let i = 0; i < mdFiles.length; i++) {
    const fileContent = fs.readFileSync(
      path.join(blogsDirectory, mdFiles[i].name),
      "utf-8"
    );
    const { data, content } = matter(fileContent);

    const slug = replaceAll(replaceAll(data.title, ",", "-"), " ", "-");

    let paramObj = {
      params: {
        blogPage: `${slug}`,
      },
    };
    paths.push(paramObj);
  }
  return {
    paths: [...paths],
    fallback: false, // can also be true or 'blocking'
  };
}

// `getStaticPaths` requires using `getStaticProps`
export async function getStaticProps({ params }) {
  const { blogPage } = params;
  const mdFiles = fs.readdirSync(blogsDirectory, {
    withFileTypes: true,
  });

  let blogsData = [];

  for (let i = 0; i < mdFiles.length; i++) {
    const fileContent = fs.readFileSync(
      path.join(blogsDirectory, mdFiles[i].name),
      "utf-8"
    );

    const { data, content } = matter(fileContent);

    const slug = replaceAll(replaceAll(data.title, ",", "-"), " ", "-");
    const mdxSource = await serialize(content);

    blogsData.push({
      data,
      mdxSource,
      slug,
    });
  }

  const blogsListByDate = blogsData
    .filter(blog => blog)
    .sort((a, b) => new Date(b.data?.date) - new Date(a.data?.date));
  const blogsBySlug = blogsListByDate.filter(blog => {
    return blog.slug === `${blogPage}`;
  });
  const stringified = JSON.stringify({
    blogBySlug: blogsBySlug[0],
    recentPosts: blogsListByDate
      .filter(blog => blog.slug !== `${blogPage}`)
      .sort(() => Math.random() - 0.5)
      .slice(0, 3),
    searchData: blogsListByDate,
  });

  return {
    props: {
      content: stringified,
    },
  };
}

const blockedChars = ["%", ":", "@", "?", "^", "&", "#", "--", "[", "]"];
function replaceAll(str, replaceKey, replaceWith) {
  const s = str
    .replace(/ *\([^)]*\) */g, "")
    .split(replaceKey)
    .map(e => e.trim())
    .filter(e => e)
    .filter(e => e !== replaceWith)
    .map(e => {
      return blockedChars.reduce((newStr, key) => {
        return newStr.trim().split(key).join("");
      }, e);
    })
    .filter(e => e)
    .join(replaceWith)
    .toLocaleLowerCase();

  return s;
}

export default function BlogPage({ content = null }) {
  const JSONdata = JSON.parse(content);
  const post = JSONdata.blogBySlug ?? {};
  const recentPosts = JSONdata.recentPosts ?? [];

  const blogContentRef = useRef(null);

  const router = useRouter();

  const schemaCodes = [
    {
      type: "application/ld+json",
      code: JSON.stringify(generateArticleSchema(post, router.asPath)),
    },
  ];

  const formatDate = blogDate => {
    const date = new Date(blogDate);
    return date.toLocaleDateString("en-us", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  };

  return (
    <Layout>
      <SEO
        title={
          post.data?.metaTitle || post.data?.customTitle || post.data?.title
        }
        useTitleTemplate={false}
        description={post.data?.metaDescription || post.data?.description}
        keywords={post.data?.metaTags || post.data?.tags}
        url={router.pathname}
        schemaCodes={schemaCodes}
        image={post.data?.image}
        ogType={"article"}
      />

        <div className={classes.container}>
          <div className={classes.coverImage}>
            {post?.data?.image && (
              <Image
                src={post?.data?.image}
                title={
                  post?.data?.imageTitle ||
                  post?.data?.customTitle ||
                  post?.data?.title
                }
                alt={
                  post?.data?.imageAlt ||
                  post?.data?.customTitle ||
                  post?.data?.title
                }
                fill={true}
                style={{
                  objectFit: "fill",
                }}
                priority={true}
              />
            )}
            {/* <p className="blog-banner-image-caption">
              {post?.data?.imageCaption}
            </p> */}
          </div>
          <div className={classes.blogContainer}>
            <h1 className={classes.header}>
              {post?.data?.customTitle ?? post?.data?.title}
            </h1>
            <div className={classes.dateFlex}>
              <p>{post?.data?.author}</p>
              {post?.data?.dateModified ? (
                <p>Updated on {formatDate(post?.data?.date)}</p>
              ) : (
                <p>Published on {formatDate(post?.data?.date)}</p>
              )}
            </div>
            {post?.mdxSource && (
              <div className={classes.blogText} ref={blogContentRef}>
                <MDXRemote
                  {...post?.mdxSource}
                  components={{
                    // ItemListSchema: props => (
                    //   <ItemListSchema post={post.data} {...props} />
                    // ),
                    BlogImage,
                    DownloadBtn: props => <DownloadBtn {...props} />,
                    FAQ: props => <FAQ {...props} />,
                  }}
                />
              </div>
            )}
          </div>
          {/* <SocialShare blog={post.data} /> */}
        <BlogList title="Similar Blogs" blogs={recentPosts} />
        </div>
    </Layout>
  );
}
